import { useEffect, useState, useCallback } from "react";
import { getSubscriberEmail, getSubscriptionStatus } from "@/lib/api";

/**
 * React hook returning the current visitor's £8.88/month membership state.
 *
 * Identity is a single email persisted in localStorage under
 * `ep_subscriber_email` (see api.js). No login flow — anyone typing their
 * email into the subscribe form or a reading form is remembered.
 *
 * Returns { active, loading, email, row, refresh }
 *  - active: `true` when Stripe reports status in {active, trialing, past_due}
 *  - row:    the raw subscription doc from /api/subscription/status
 *  - refresh(): re-fetch on demand (e.g. after purchase / cancel)
 */
export default function useSubscription() {
  const [email, setEmail] = useState(getSubscriberEmail());
  const [row, setRow] = useState(null);
  const [loading, setLoading] = useState(!!email);

  const refresh = useCallback(async () => {
    const current = getSubscriberEmail();
    setEmail(current);
    if (!current) { setRow(null); setLoading(false); return; }
    setLoading(true);
    try {
      const r = await getSubscriptionStatus({ email: current });
      setRow(r);
    } catch (_) {
      setRow(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  // Re-check when window regains focus so a fresh subscribe / cancel
  // taken in a different tab appears immediately.
  useEffect(() => {
    const onFocus = () => refresh();
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [refresh]);

  return {
    active: !!row?.active,
    row,
    loading,
    email,
    refresh,
  };
}
